import type { ProjectDocumentDetail } from "@shared/types";
import { Clock, Download, ExternalLink, History, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	downloadProjectDocument,
	getProjectDocument,
	openProjectDocument,
} from "../../services/project-documents-api.service";

interface DocumentVersionHistoryDialogProps {
	projectId: string;
	documentId: string | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onVersionUpload: (documentId: string, file: File) => Promise<void>;
}

function formatDateTime(value: string): string {
	return new Date(value).toLocaleString("en-IN");
}

export function DocumentVersionHistoryDialog({
	projectId,
	documentId,
	open,
	onOpenChange,
	onVersionUpload,
}: DocumentVersionHistoryDialogProps): React.ReactElement {
	const [document, setDocument] = useState<ProjectDocumentDetail | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [versionFile, setVersionFile] = useState<File | null>(null);

	useEffect(() => {
		if (!open || !documentId) return;

		let active = true;
		setIsLoading(true);
		void getProjectDocument(projectId, documentId)
			.then((response) => {
				if (active) {
					setDocument(response.document);
				}
			})
			.catch(() => {
				if (active) {
					toast.error("Failed to load document history");
				}
			})
			.finally(() => {
				if (active) {
					setIsLoading(false);
				}
			});

		return () => {
			active = false;
		};
	}, [open, projectId, documentId]);

	const handleUpload = async () => {
		if (!documentId || !versionFile) return;
		setIsUploading(true);
		try {
			await onVersionUpload(documentId, versionFile);
			const response = await getProjectDocument(projectId, documentId);
			setDocument(response.document);
			setVersionFile(null);
			toast.success("Document version uploaded");
		} catch {
			toast.error("Failed to upload document version");
		} finally {
			setIsUploading(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<History className="h-5 w-5" />
						Version history
					</DialogTitle>
					<DialogDescription>
						Track immutable versions for this document and upload the next one.
					</DialogDescription>
				</DialogHeader>
				{isLoading ? (
					<div className="space-y-3">
						<Skeleton className="h-14 w-full" />
						<Skeleton className="h-14 w-full" />
						<Skeleton className="h-14 w-full" />
					</div>
				) : document ? (
					<div className="space-y-4">
						<div className="rounded-lg border bg-muted/30 p-4">
							<div className="flex flex-wrap items-center justify-between gap-3">
								<div>
									<p className="font-semibold text-foreground">
										{document.title}
									</p>
									<p className="text-sm text-muted-foreground">
										{document.type} • Uploaded by{" "}
										{document.createdByName ?? "Unknown"}
									</p>
								</div>
								<span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium uppercase tracking-wide text-primary">
									Current v{document.latestVersion.versionNumber}
								</span>
							</div>
						</div>
						<div className="space-y-2">
							{document.versions.map((version) => (
								<div
									key={version.id}
									className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
								>
									<div>
										<div className="flex items-center gap-2">
											<p className="font-medium text-foreground">
												Version {version.versionNumber}
											</p>
											{version.isCurrent && (
												<span className="rounded-full bg-dashboard-pill-done-bg px-2 py-0.5 text-xs font-medium text-dashboard-success">
													Current
												</span>
											)}
										</div>
										<p className="text-sm text-muted-foreground">
											{version.originalFileName}
										</p>
										<p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
											<Clock className="h-3.5 w-3.5" />
											{formatDateTime(version.createdAt)} by{" "}
											{version.uploadedByName ?? "Unknown"}
										</p>
									</div>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												void openProjectDocument(version.downloadUrl)
											}
										>
											<ExternalLink className="h-4 w-4" />
											Open
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												void downloadProjectDocument(
													version.downloadUrl,
													version.originalFileName,
												)
											}
										>
											<Download className="h-4 w-4" />
											Download
										</Button>
									</div>
								</div>
							))}
						</div>
						<div className="rounded-lg border border-dashed p-4">
							<p className="mb-3 text-sm font-medium text-foreground">
								Upload a new version
							</p>
							<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
								<Input
									aria-label="Version file"
									type="file"
									accept=".pdf,image/jpeg,image/png,image/webp"
									onChange={(event) =>
										setVersionFile(event.target.files?.[0] ?? null)
									}
								/>
								<Button
									onClick={() => void handleUpload()}
									isLoading={isUploading}
									disabled={!versionFile}
								>
									<Upload className="h-4 w-4" />
									Upload version
								</Button>
							</div>
						</div>
					</div>
				) : (
					<p className="text-sm text-muted-foreground">
						No document history available.
					</p>
				)}
				<DialogFooter>
					<Button variant="ghost" onClick={() => onOpenChange(false)}>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
